import numeral from 'numeral';
import moment from 'moment';
import { getTimeValues } from '../../../js/utils/timeValuesUtils';
import store from '../../../store/index';

const numeralFormat = '0,0.00';

const formatNumber = (number, digits = 2) => {
	if (number) {
		return parseFloat(number.toFixed(digits));
	}
	return number;
};

export default class ProductDataFormatter {
	constructor(fertilizationData) {
		this.fertilization = fertilizationData;
		this.plantation = null;
		this.productResume = null;
		this.stackProduct = [];
		this.nutritionIrrigationStepData = null;
	}

	async buildProductResumeData() {
		this.plantation = store.getters['RegisterPlantation/actualPlantation'];
		this.nutritionIrrigationStepData =
			store.getters['nutritionStepper/nutritionIrrigationData'];
		this.productResume = this.fertilization.contribution_products.map(
			(product) => {
				const isSolid = product?.presentation === 'SOLIDO';
				const plantsDensityRaw = this.plantation.plants_density;
				const plants_density = Number(plantsDensityRaw);
				const hasValidDensity =
					Number.isFinite(plants_density) && plants_density > 0;

				const area = this.fertilization.area;
				const numPlants = this.plantation.plants || 1;
				const product_density = product?.density || 1;
				const dosageUnit = formatNumber(
					product.periods.reduce((a, b) => a + b, 0),
				);
				const priceKg = formatNumber(product.price_per_kg, 3);
				const priceL = formatNumber(product.price_per_liter, 3);
				let dosageUnitKg = 0;
				let dosageUnitL = 0;
				let dosagePlant = 0;
				let dosagePlantKg = 0;
				let dosagePlantL = 0;
				let dosageTotal = 0;
				let dosageTotalKg = 0;
				let dosageTotalL = 0;
				let costUnit = 0;
				let costPlant = 0;
				let costTotal = 0;

				if (this.nutritionIrrigationStepData.dosis_unit == 1) {
					dosageUnitKg = this.roundValue(
						isSolid ? dosageUnit : dosageUnit * (product?.density || 1),
						1,
					);
					dosageUnitL = isSolid ? '' : dosageUnit;
					dosagePlant = hasValidDensity
						? this.roundValue((dosageUnit * 1000) / plants_density, 1)
						: null;
					dosagePlantKg = this.roundValue(
						(dosageUnitKg * 1000) / plants_density,
						1,
					);
					dosagePlantL = isSolid
						? ''
						: this.roundValue((dosageUnitL * 1000) / plants_density, 1);
					dosageTotal = this.roundValue(dosageUnit * area, 1);
					dosageTotalKg = this.roundValue(dosageUnitKg * area, 1);
					dosageTotalL = isSolid ? '' : this.roundValue(dosageUnitL * area, 1);
					if (!isSolid && priceL > 0) {
						costUnit = this.roundValue(dosageUnitL * priceL, 2);
					} else {
						costUnit = this.roundValue(dosageUnitKg * priceKg, 2);
					}
					costTotal = this.roundValue(costUnit * area, 2);
					costPlant =
						numPlants > 0 ? this.roundValue(costTotal / numPlants, 2) : 0;
				} else if (this.nutritionIrrigationStepData.dosis_unit == 2) {
					if (isSolid) {
						dosageUnitKg = dosageUnit;
					} else {
						dosageUnitKg = this.roundValue(
							dosageUnit * (product?.density || 1),
							1,
						);
						dosageUnitL = dosageUnit;
						dosagePlantL = dosageUnit;
						dosageTotalL = this.roundValue(
							(dosagePlantL * numPlants) / 1000,
							1,
						);
					}
					dosagePlantKg = dosageUnitKg;
					dosageTotalKg = this.roundValue((dosageUnitKg * numPlants) / 1000, 1);

					if (!isSolid && priceL > 0) {
						costUnit = this.roundValue((dosageUnitL * priceL) / 1000, 2);
					} else {
						costUnit = this.roundValue((dosageUnitKg * priceKg) / 1000, 2);
					}
					costPlant = costUnit;
					costTotal = this.roundValue(costUnit * numPlants, 2);
				}
				return {
					id: product.product,
					product: product.product_name,
					status: product.product_status,
					density: product.density,
					presentation: product.presentation,
					tipology: product.tipology,

					// Dosis
					dosageUnit,
					dosageUnitKg,
					dosageUnitL,

					// Dosis por planta
					dosagePlant,
					dosagePlantKg,
					dosagePlantL,

					// Dosis total
					dosageTotal,
					dosageTotalKg,
					dosageTotalL,

					price: formatNumber(product.price),
					priceKg,
					priceL,

					costUnit,
					costPlant,
					costTotal,
				};
			},
		);

		const { timeValues } = getTimeValues(
			moment(this.fertilization.init_date, 'YYYY/MM/DD'),
			moment(this.fertilization.end_date, 'YYYY/MM/DD'),
			this.fertilization.period,
			false,
			true,
		);

		const notFoundProducts = [];

		this.productResume.forEach((product, i) => {
			this.productResume[i].status = product?.status ?? true;

			this.fertilization.contribution_products[i].periods.forEach(
				(period, indexPeriod) => {
					this.stackProduct.push({
						productName: product.product,
						monthWeek: timeValues[indexPeriod],
						periodIndex: indexPeriod,
						value: period,
					});
				},
			);
		});

		this.productResume.push({
			product: 'Total',
			dosageUnit: '',
			dosagePlant: '',
			dosageTotal: '',
			price: '',
			status: '',
			costUnit: formatNumber(this.sumValue('costUnit')),
			costPlant: formatNumber(this.sumValue('costPlant')),
			costTotal: formatNumber(this.sumValue('costTotal')),
		});

		if (notFoundProducts.length > 0)
			throw new Error(JSON.stringify(notFoundProducts));
		return this.productResume;
	}
	calculateTotals() {
		this.productResume.push({
			product: this.$t('nutritionDetail.productResume.total'),
			dosageUnit: '',
			dosagePlant: '',
			dosageTotal: '',
			price: '',
			costUnit: numeral(this.sumValue('costUnit')).format(numeralFormat),
			costPlant: numeral(this.sumValue('costPlant')).format(numeralFormat),
			costTotal: numeral(this.sumValue('costTotal')).format(numeralFormat),
		});
	}

	numOr0(number) {
		return number === undefined ? 0 : numeral(number).value();
	}

	sumValue(prop) {
		return this.productResume.reduce(
			(a, b) => ({ [prop]: this.numOr0(a[prop]) + this.numOr0(b[prop]) }),
			{ [prop]: this.numOr0(0) },
		)[prop];
	}
	roundValue(val, decimals = 2) {
		return parseFloat(val.toFixed(decimals));
	}
}
